// Release C-2 (P3) · Communications Workspace shell.
// Gates on CONNECTED provider state; renders honest AUTHORIZATION_REQUIRED
// stopping point per directive when Entra is not yet configured / consented.
// Owner + Administrator only.
import React, { useEffect, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Mail, ExternalLink, Shield } from "lucide-react";

export default function Communications() {
  const [state, setState] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [pickedUpn, setPickedUpn] = useState(null);
  const [messages, setMessages] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, m] = await Promise.all([
          api.get("/admin/integrations/microsoft365/state"),
          api.get("/admin/integrations/microsoft365/mailboxes"),
        ]);
        setState(s.data);
        const items = m.data.items || [];
        setMailboxes(items);
        const first = items.find((x) => x.chip_state === "CONNECTED");
        if (first) setPickedUpn(first.upn);
      } catch (e) {
        setErr(e?.response?.data?.detail?.message || "Failed to load Communications");
      }
    })();
  }, []);

  useEffect(() => {
    if (!pickedUpn) { setMessages(null); return; }
    (async () => {
      setBusy(true);
      try {
        const r = await api.get("/admin/integrations/microsoft365/messages",
          { params: { mailbox_upn: pickedUpn, top: 25 } });
        setMessages(r.data.items || []);
      } catch (e) {
        const d = e?.response?.data?.detail;
        setErr(d?.message || d?.code || "Failed to list messages");
        setMessages([]);
      } finally { setBusy(false); }
    })();
  }, [pickedUpn]);

  const openMessage = async (m) => {
    setSelected(m);
    setDetail(null);
    try {
      const r = await api.get(
        `/admin/integrations/microsoft365/messages/${m.id}`,
        { params: { mailbox_upn: pickedUpn } });
      setDetail(r.data);
    } catch (e) {
      setDetail({ __error: e?.response?.data?.detail?.message || "load failed" });
    }
  };

  const providerState = state?.provider_state;
  const gated = providerState !== "CONNECTED";

  return (
    <AdminAppShell current="/admin/communications">
      <div data-testid="communications-page" style={{ padding: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Communications</h1>
        <div style={{ color: "#475569", fontSize: 13, marginTop: 4, marginBottom: 20 }}>
          Delegated Microsoft 365 view. ConstructionOS never stores full message bodies —
          only associations + minimal metadata. Explicit send required; never auto-sends.
        </div>

        {gated && (
          <Card data-testid="comms-gated" style={{ marginBottom: 20, background: "#FEF3C7", borderColor: "#FCD34D" }}>
            <CardContent style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Shield size={28} color="#92400E" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#92400E", fontWeight: 700 }}>
                    Authorization Required
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#78350F", marginTop: 2 }}>
                    Microsoft 365 is not yet connected for this tenant.
                  </div>
                  <div style={{ fontSize: 13, color: "#78350F", marginTop: 4 }}>
                    The Communications workspace unlocks once at least one approved mailbox
                    is <b>Connected</b>. Complete the Entra registration and consent flow
                    from Communications Settings.
                  </div>
                </div>
                <Link to="/admin/settings/communications">
                  <Button data-testid="comms-goto-settings">Go to Communications Settings →</Button>
                </Link>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "#78350F" }}>
                Current provider state: <b>{providerState || "loading…"}</b>
              </div>
            </CardContent>
          </Card>
        )}

        {!gated && (
          <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 1.2fr", gap: 12 }}>
            <Card data-testid="comms-mailbox-rail">
              <CardContent style={{ padding: 14 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1, marginBottom: 8 }}>
                  Mailboxes
                </div>
                {mailboxes.length === 0 && (
                  <div style={{ fontSize: 12, color: "#64748b" }}>No approved mailboxes yet.</div>
                )}
                {mailboxes.map((m) => {
                  const canPick = m.chip_state === "CONNECTED";
                  return (
                    <button
                      key={m.id}
                      data-testid={`comms-mailbox-${m.id}`}
                      onClick={() => canPick && setPickedUpn(m.upn)}
                      disabled={!canPick}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "8px 10px", borderRadius: 4, marginBottom: 6,
                        background: pickedUpn === m.upn ? "#12233F" : "white",
                        color: pickedUpn === m.upn ? "#F5F1E8" : "#12233F",
                        border: "1px solid #cbd5e1",
                        cursor: canPick ? "pointer" : "not-allowed",
                        opacity: canPick ? 1 : 0.55,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{m.upn}</div>
                      <div style={{ fontSize: 10, marginTop: 2 }}>
                        {m.kind} · {m.chip_state}
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            <Card data-testid="comms-message-list">
              <CardContent style={{ padding: 14 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1, marginBottom: 8 }}>
                  Inbox {pickedUpn ? `· ${pickedUpn}` : ""}
                </div>
                {busy && <div style={{ color: "#64748b", fontSize: 12 }}>Loading messages…</div>}
                {!busy && messages && messages.length === 0 && (
                  <div style={{ color: "#64748b", fontSize: 12 }}>No messages returned by Graph.</div>
                )}
                {(messages || []).map((m) => (
                  <button
                    key={m.id}
                    data-testid={`comms-msg-${m.id}`}
                    onClick={() => openMessage(m)}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "8px 10px", borderTop: "1px solid #e5e7eb",
                      background: selected?.id === m.id ? "#FEF3C7" : "white",
                      border: "none", cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: m.isRead ? 400 : 700 }}>
                      {m.subject || "(no subject)"}
                    </div>
                    <div style={{ fontSize: 11, color: "#475569" }}>
                      {m.from?.emailAddress?.address || "—"} · {m.receivedDateTime}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      {(m.bodyPreview || "").slice(0, 100)}
                    </div>
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="comms-message-detail">
              <CardContent style={{ padding: 14, minHeight: 400 }}>
                {!selected && (
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Select a message to view its metadata + sanitized body preview here.
                    ConstructionOS never persists the body — closing this pane drops it.
                  </div>
                )}
                {selected && !detail && (
                  <div style={{ color: "#64748b", fontSize: 12 }}>Fetching message…</div>
                )}
                {selected && detail && (
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{detail.subject}</div>
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
                      From: <b>{detail.from?.emailAddress?.address || "—"}</b>
                    </div>
                    <div style={{ fontSize: 12, color: "#475569" }}>
                      Received: {detail.receivedDateTime}
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                      <a
                        href={detail.webLink || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12, color: "#12233F", textDecoration: "none",
                                  border: "1px solid #cbd5e1", padding: "4px 10px", borderRadius: 4,
                                  display: "inline-flex", alignItems: "center", gap: 6 }}
                        data-testid="comms-open-in-outlook"
                      >
                        <ExternalLink size={12} /> Open in Outlook
                      </a>
                      <Button
                        size="sm" variant="outline"
                        data-testid="comms-associate-btn"
                        onClick={() => alert("Associate flow: pick record type + record id, confirm. Metadata only; original body never rewritten.")}
                      >
                        Associate to ERF record
                      </Button>
                    </div>
                    <div style={{ marginTop: 12, padding: 10, background: "#F5F1E8",
                                    borderRadius: 4, fontSize: 12, color: "#12233F",
                                    whiteSpace: "pre-wrap", maxHeight: 320, overflow: "auto" }}
                          data-testid="comms-sanitized-body">
                      {(detail.body?.content || detail.bodyPreview || "").replace(/<script[^>]*>.*?<\/script>/gi, "").slice(0, 4000)}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 10, color: "#64748b" }}>
                      Body sanitized inline. Remote content blocked until you opt in per message.
                    </div>
                  </div>
                )}
                {selected && detail?.__error && (
                  <div style={{ color: "#B91C1C", fontSize: 12 }}>Error: {detail.__error}</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {err && (
          <div data-testid="comms-error" style={{ color: "#B91C1C", fontSize: 12, marginTop: 12 }}>
            {err}
          </div>
        )}
      </div>
    </AdminAppShell>
  );
}
